import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'
import { ClubButton, Crest, Field } from './golf'

export type MagicLinkMode = 'apply' | 'signin' | 'invite'

/**
 * The doors this card stands in. `apply` is /join, step I of the
 * application; `signin` is /sign-in, the members' entrance; `invite` is the
 * Quartermaster accept page, where signing in is the one step between a
 * standing offered and a standing held.
 *
 * INVARIANT: mode swaps WORDS ONLY — never markup, never classes, never a
 * conditional. The structure below is identical in every mode, which is what
 * stops the focus management in this file from diverging between them. If a
 * mode ever needs different markup, it needs its own component, not a branch.
 *
 * The heading's LEVEL is not the mode's to set, and is the one thing the
 * embedding page decides (`headingLevel`): on /join and /sign-in this card's
 * heading is the page's h1; on the Quartermaster invitation the page already
 * has its h1 above the card, and the card draws its heading as an h2
 * (the-quartermaster-invite.html:299). Same element in every state and every
 * mode, same words — only the number changes.
 */
const COPY: Record<MagicLinkMode, { heading: string; lede: React.ReactNode; hint: string }> = {
  apply: {
    heading: 'Apply for membership',
    lede: (
      <>
        One address is all the paperwork.{' '}
        <b className="font-semibold text-fairway">Founding seats</b> — the first hundred, rate held
        for life — are seating now.
      </>
    ),
    hint: 'A sign-in link, no password. Discretion assured.',
  },
  signin: {
    heading: 'Welcome back.',
    lede: (
      <>
        The club keeps no passwords. Give us the address on your card and a link follows — good for
        fifteen minutes.
      </>
    ),
    hint: 'The link works once. Discretion assured.',
  },
  invite: {
    heading: 'Sign in to take it up',
    lede: (
      <>
        The club keeps no passwords. Give us your address and a sign-in link follows — good for
        fifteen minutes, and your acceptance is one press beyond it.
      </>
    ),
    hint: 'A sign-in link, no password. Discretion assured.',
  },
}

/**
 * Magic-link email sign-in (build-contract §3.1): idle → sending → sent, with
 * a recoverable inline error. The endpoint answers uniformly whether or not
 * the address exists (no enumeration), so the sent state is unconditional —
 * "check your email" is all we can honestly say.
 *
 * Renders the club's card whole — seal, heading and control — in both states,
 * because the design ties the heading to the state (the mode's heading becomes
 * "Check your gate."). The screens supply only the chrome around it.
 *
 * One component serves both doors because there is no second BEHAVIOR to
 * justify a second one: same endpoint, same payload, same fifteen minutes —
 * find_or_create_email_user means one POST signs in and signs up alike. Only
 * the words differ, and the sent state does not differ even in those.
 */
export function MagicLinkSignIn({
  mode = 'apply',
  headingLevel = 1,
}: {
  mode?: MagicLinkMode
  /** 1 where this card IS the page's heading (/join, /sign-in); 2 under a page that has one. */
  headingLevel?: 1 | 2
}) {
  const copy = COPY[mode]
  const Heading = headingLevel === 2 ? 'h2' : 'h1'
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const sentRef = useRef<HTMLDivElement>(null)

  // Move focus to the confirmation when it appears (WCAG 4.1.3 / 2.4.3): a live
  // region mounted together with its text isn't reliably announced, and
  // disabling the focused control on submit drops focus to <body>. Focusing the
  // sent panel both announces its status text and returns keyboard focus.
  useEffect(() => {
    if (sent) sentRef.current?.focus()
  }, [sent])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSending(true)
    setError(null)
    try {
      await api.post('/auth/magic_link', { email })
      setSent(true)
    } catch (err) {
      // ApiError carries the contract message ({ error: { message } }).
      setError(err instanceof Error ? err.message : "We couldn't send the link. Try again?")
    } finally {
      setSending(false)
    }
  }

  const reset = () => {
    setSent(false)
    setEmail('')
    // Return focus to the field the ghost button just replaced.
    requestAnimationFrame(() => emailRef.current?.focus())
  }

  if (sent) {
    return (
      <div ref={sentRef} tabIndex={-1} className="flex flex-1 flex-col text-center outline-none">
        <Crest monogram className="mx-auto mb-5 size-[66px]" />
        <Heading className="font-display text-3xl font-medium tracking-[-0.01em] text-pine">
          Check your gate.
        </Heading>
        <p className="mx-auto mb-6.5 mt-3 max-w-[32ch] text-[15px] text-ink-soft" role="status">
          We&rsquo;ve posted a sign-in link to{' '}
          <strong className="font-semibold text-fairway">{email}</strong>. It expires in fifteen
          minutes.
        </p>
        <div className="mt-auto">
          <ClubButton type="button" variant="ghost" className="w-full" onClick={reset}>
            Use a different address
          </ClubButton>
          <p className="mt-4 text-xs text-ink-soft">
            Nothing yet? Give it a minute — then look in the spam folder.
          </p>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-1 flex-col text-center">
      <Crest monogram className="mx-auto mb-5 size-[66px]" />
      <Heading className="font-display text-3xl font-medium tracking-[-0.01em] text-pine">
        {copy.heading}
      </Heading>
      <p className="mx-auto mb-6.5 mt-3 max-w-[36ch] text-[14.5px] text-ink-soft">{copy.lede}</p>

      <div className="text-left">
        <Field
          ref={emailRef}
          label="Email address"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          disabled={sending}
          error={error ?? undefined}
        />
      </div>

      <div className="mt-auto">
        <ClubButton type="submit" disabled={sending} className="w-full">
          {sending ? 'Sending…' : 'Email me a sign-in link'}
        </ClubButton>
        <p className="mt-4 text-xs text-ink-soft">{copy.hint}</p>
      </div>
    </form>
  )
}
